import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { FiDownload, FiPlusSquare, FiShare, FiX } from "react-icons/fi";

import styles from "./PwaInstallPrompt.module.css";

const DISMISS_STORAGE_KEY = "sfarm_pwa_install_prompt_dismissed_at";
const LAST_SHOWN_STORAGE_KEY = "sfarm_pwa_install_prompt_last_shown_at";
const SHOWN_SESSION_KEY = "sfarm_pwa_install_prompt_shown_this_session";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const REPEAT_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 5000;

const isStandaloneMode = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true ||
  document.referrer.startsWith("android-app://");

const isIosDevice = () => {
  const userAgent = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";

  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
};

const isAndroidDevice = () =>
  /Android/i.test(window.navigator.userAgent || "");

const wasDismissedRecently = () => {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_STORAGE_KEY) || 0);
    return dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

const wasShownRecently = () => {
  try {
    const shownThisSession = sessionStorage.getItem(SHOWN_SESSION_KEY) === "1";
    const lastShownAt = Number(localStorage.getItem(LAST_SHOWN_STORAGE_KEY) || 0);

    return (
      shownThisSession ||
      (lastShownAt > 0 && Date.now() - lastShownAt < REPEAT_INTERVAL_MS)
    );
  } catch {
    return false;
  }
};

const rememberPromptShown = () => {
  try {
    sessionStorage.setItem(SHOWN_SESSION_KEY, "1");
    localStorage.setItem(LAST_SHOWN_STORAGE_KEY, String(Date.now()));
  } catch {
    // Showing the prompt must not depend on storage availability.
  }
};

const PwaInstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showManualGuide, setShowManualGuide] = useState(false);

  const isSupportedPage = useMemo(
    () =>
      location.pathname === "/" ||
      location.pathname === "/dashboard" ||
      location.pathname.startsWith("/monitor/"),
    [location.pathname],
  );

  const ios = useMemo(isIosDevice, []);
  const android = useMemo(isAndroidDevice, []);

  useEffect(() => {
    if (
      !isSupportedPage ||
      isStandaloneMode() ||
      wasDismissedRecently() ||
      wasShownRecently()
    ) {
      setIsVisible(false);
      return undefined;
    }

    let showTimer = null;

    const scheduleShow = () => {
      if (showTimer) return;
      showTimer = window.setTimeout(() => {
        rememberPromptShown();
        setIsVisible(true);
      }, SHOW_DELAY_MS);
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      scheduleShow();
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setIsVisible(false);
      setShowManualGuide(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    if (ios || android) {
      scheduleShow();
    }

    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, [android, ios, isSupportedPage]);

  const dismissPrompt = () => {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    } catch {
      // The prompt can still be closed when storage is unavailable.
    }

    setIsVisible(false);
    setShowManualGuide(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();

      try {
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === "accepted") {
          setIsVisible(false);
        }
      } finally {
        setDeferredPrompt(null);
      }

      return;
    }

    if (ios || android) {
      setShowManualGuide(true);
    }
  };

  if (!isVisible || !isSupportedPage || isStandaloneMode()) return null;

  return createPortal(
    <>
      <aside className={styles.banner} dir="rtl" role="status">
        <div className={styles.iconBox} aria-hidden="true">
          <FiDownload />
        </div>

        <div className={styles.copy}>
          <strong>نصب برنامه sfarm</strong>
          <span>برای دسترسی سریع‌تر، برنامه را به صفحه اصلی اضافه کنید.</span>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.installButton} onClick={handleInstall}>
            {!deferredPrompt ? "راهنمای نصب" : "نصب برنامه"}
          </button>
          <button type="button" className={styles.laterButton} onClick={dismissPrompt}>
            بعداً
          </button>
        </div>

        <button
          type="button"
          className={styles.closeButton}
          onClick={dismissPrompt}
          aria-label="بستن پیشنهاد نصب"
        >
          <FiX />
        </button>
      </aside>

      {showManualGuide && (
        <div className={styles.modalBackdrop} onMouseDown={() => setShowManualGuide(false)}>
          <div
            className={styles.modal}
            dir="rtl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sfarm-ios-install-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => setShowManualGuide(false)}
              aria-label="بستن راهنمای نصب"
            >
              <FiX />
            </button>

            <h2 id="sfarm-ios-install-title">
              افزودن sfarm به صفحه اصلی {ios ? "آیفون" : "اندروید"}
            </h2>

            {ios ? (
              <ol className={styles.steps}>
                <li>
                  <span className={styles.stepIcon}><FiShare /></span>
                  در نوار Safari روی دکمه اشتراک‌گذاری بزنید.
                </li>
                <li>
                  <span className={styles.stepIcon}><FiPlusSquare /></span>
                  گزینه <b>Add to Home Screen</b> را انتخاب کنید.
                </li>
                <li>
                  <span className={styles.stepNumber}>۳</span>
                  در بالای صفحه روی <b>Add</b> بزنید.
                </li>
              </ol>
            ) : (
              <ol className={styles.steps}>
                <li>
                  <span className={styles.stepNumber}>۱</span>
                  منوی مرورگر Chrome را از علامت سه‌نقطه باز کنید.
                </li>
                <li>
                  <span className={styles.stepIcon}><FiPlusSquare /></span>
                  گزینه <b>Install app</b> یا <b>Add to Home screen</b> را بزنید.
                </li>
                <li>
                  <span className={styles.stepNumber}>۳</span>
                  نصب برنامه را تأیید کنید.
                </li>
              </ol>
            )}

            <button
              type="button"
              className={styles.doneButton}
              onClick={dismissPrompt}
            >
              متوجه شدم
            </button>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
};

export default PwaInstallPrompt;
